import React from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { TIER_COLOR, TIER_LABEL } from "./systemMapping";

/**
 * 5-segment cumulative tier bar. Per Figma node 14711:147323.
 *
 * Behavior:
 *   - Each segment represents 20% of the score (5 × 20% = 100%).
 *   - Full segments below the active fill = fully coloured with the tier
 *     colour. e.g. score 85 → 4 full segments + 25% of segment 5.
 *   - Partial fill = (score % 20) / 20 of the next segment.
 *   - Segments above the partial fill stay gray.
 *   - Active label (driven by `scoreCategory`, NOT `score`) is rendered in
 *     `NEUTRAL_TEXT_WEAK`; the rest are `NEUTRAL_BORDER_DEFAULT`.
 *
 * Color: from `TIER_COLOR[scoreCategory]` (1–5 backend scale). Score and
 * scoreCategory may not always agree on which segment is "active" — colour
 * always wins from scoreCategory; visual fill always wins from score.
 */
type Props = {
  score: number;
  scoreCategory: number;
};

const TIERS = [1, 2, 3, 4, 5] as const;
const SEGMENT_PCT = 20;

const HealthScoreBar: React.FC<Props> = ({ score, scoreCategory }) => {
  const theme: Theme = useTheme();
  const tierColors = TIER_COLOR(theme);
  const activeLabel = Math.max(1, Math.min(5, scoreCategory));
  const color = tierColors[activeLabel];

  const clampedScore = Math.max(0, Math.min(100, score));
  const fullSegments = Math.floor(clampedScore / SEGMENT_PCT);
  const partialPct = (clampedScore % SEGMENT_PCT) / SEGMENT_PCT;

  return (
    <Box sx={styles.wrap}>
      <Box sx={styles.bar}>
        {TIERS.map((_t, i) => {
          const isFull = i < fullSegments;
          const isPartial = i === fullSegments && partialPct > 0;
          return (
            <Box
              key={i}
              sx={styles.cellWrap(theme, isFull ? color : null)}
            >
              {isPartial && (
                <Box
                  sx={{ ...styles.cellActiveFill(color), width: `${partialPct * 100}%` }}
                />
              )}
            </Box>
          );
        })}
      </Box>
      <Box sx={styles.labels}>
        {TIERS.map((t) => (
          <Box
            component="span"
            key={t}
            sx={styles.label(theme, t === activeLabel)}
          >
            {TIER_LABEL[t]}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default HealthScoreBar;

const styles = {
  wrap: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "4.5px",
    width: "100%",
  },
  bar: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "4px",
    height: "4px",
  },
  cellWrap: (theme: Theme, fillColor: string | null) => ({
    position: "relative" as const,
    height: "4px",
    borderRadius: "8px",
    backgroundColor: fillColor ?? theme.colors_ds.NEUTRAL_BORDER_WEAK,
  }),
  cellActiveFill: (color: string) => ({
    position: "absolute" as const,
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: "8px",
    backgroundColor: color,
  }),
  labels: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "2px",
  },
  label: (theme: Theme, active: boolean) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    lineHeight: "15px",
    textAlign: "center" as const,
    color: active
      ? theme.colors_ds.NEUTRAL_TEXT_WEAK
      : theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
  }),
};
